import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import TouchableElement from '@components/common/touchableElement';
import Line from '@components/common/line';

import { scale as initialScale } from '@components/frame';

import ColorWrapper, { WithColor } from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import Button from '@styles/buttons';
import { getBoxShadowStyle } from '@styles/shadow';

import { modalBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

import type { OperationSearchItem } from '@interfaces/search';

import { useManageStartJobPopup } from './hooks/useManageStartJobPopup';
import SendToMachineCell from './components/SendToMachineCell';
import LinkCurrentNcFile from './components/LinkCurrentNcFile';
import StartJobHeader from './components/StartJobHeader';
import { NC_FILE_LIST_MAX_HEIGHT } from './constants';
import { SEND_STATUS } from '@helpers';
import type { ScaleFontProps } from './types';

export interface StartJobPopupProps extends WithColor, ScaleFontProps {
  hide: () => void;
  item: OperationSearchItem;
}

const StartJobPopup = (props: StartJobPopupProps) => {
  const { C, hide, item, scale = initialScale, font = initialFont } = props;

  const { sendingStorage, messageStorage, isFinish, uploadFile, list, shouldScrollNcFiles, handleSendToMachine, handleGoToJobScreen, handleLinked } =
    useManageStartJobPopup({ item, hide, scale, font });

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View style={{ width: '100%', paddingHorizontal: scale(15), paddingBottom: scale(15), backgroundColor: C(modalBg), borderRadius: scale(SCREEN_BORDER_RADIUS) }}>
      <View style={{ width: '100%', alignItems: 'center' }}>
        <View style={{ width: '100%' }}>
          <View
            style={{
              width: '100%',
              zIndex: 1000,
              height: scale(54),
            }}
          >
            <View style={{ position: 'absolute', zIndex: 1000, right: 0, top: scale(6.75) }}>
              <Button {...{ variant: 'L', type: 'Only Text Link', onPress: hide, scale, font }}>{i18n.t('Done')}</Button>
            </View>
          </View>
          <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
            <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Start Job')}</Text>
          </View>
        </View>
        <View style={{ width: '100%' }}>
          <StartJobHeader item={item} C={C} font={font} scale={scale} />
        </View>
        <View style={{ width: '100%' }}>
          <Text style={[font['Headline'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(6) }]}>
            {i18n.t('Go to the machine and select one of the NC Files below to start this Job.')}
          </Text>
        </View>
        {!!list.length && (
          <View style={{ width: '100%', marginTop: scale(18) }}>
            <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              <ScrollView style={[shouldScrollNcFiles && { maxHeight: scale(NC_FILE_LIST_MAX_HEIGHT) }]}>
                {list.map((element, index) => {
                  const status = sendingStorage[element.ncFileId];
                  const isThisFinish = !(sendingStorage[element.ncFileId] === SEND_STATUS.Finish) && isFinish;
                  return (
                    <Line
                      {...{
                        bg: 'Background/Light/Field on modal',
                        scale,
                        font,
                        takePhoneLayout: true,
                        textColor: isThisFinish ? 'Label/Light/Quinary' : 'Label/Light/Primary',
                        item: {
                          hideArrow: true,
                          name: element.ncFileName,
                          valueFunction: true,
                          value: () => (
                            <SendToMachineCell
                              element={element}
                              status={status}
                              isThisFinish={isThisFinish}
                              messageStorage={messageStorage}
                              onSend={handleSendToMachine}
                              C={C}
                              scale={scale}
                              font={font}
                            />
                          ),
                          noArrow: true,
                        },
                        index,
                        array: list,
                        C,
                        roundTop: index === 0,
                        roundBottom: index === list.length - 1,
                      }}
                      key={element?.ncFileId}
                    />
                  );
                })}
              </ScrollView>
            </View>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(3), paddingHorizontal: scale(15) }]}>
              {i18n.t('These are the NC Files linked to Job')} {item.productionOperationNumber}
            </Text>
          </View>
        )}
        {isFinish ? (
          <TouchableElement onPress={handleGoToJobScreen}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    width: '100%',
                    height: scale(53),
                    backgroundColor: C(pressHoverColor('Button/Light/Primary')),
                    alignContent: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: scale(22),
                    borderRadius: scale(12),
                  },
                ]}
              >
                <Text style={[font['Headline'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('Go To Job Screen')}</Text>
              </View>
            )}
          </TouchableElement>
        ) : (
          <View style={{ width: '100%' }}>
            <View
              style={[
                shadowStyles,
                { borderRadius: scale(SCREEN_BORDER_RADIUS) },
                {
                  width: '100%',
                  marginTop: scale(18),
                },
              ]}
            >
              {uploadFile.map((i, index) => (
                <Line
                  {...{
                    scale,
                    font,
                    key: i.name,
                    item: i,
                    index,
                    array: uploadFile,
                    C,
                    smallSize: true,
                    topBorder: false,
                    roundTop: true,
                    roundBottom: true,
                    textColor: 'Link/Light/Primary',
                  }}
                />
              ))}
            </View>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(3), paddingHorizontal: scale(15) }]}>
              {i18n.t('Add a link from this Job to another NC File')}
            </Text>
            <LinkCurrentNcFile C={C} productionOperationId={item.productionOperationId} onLinked={handleLinked} scale={scale} font={font} />
          </View>
        )}
      </View>
    </View>
  );
};

export default ColorWrapper(StartJobPopup);
